import { Constants } from '@/data/constants';
import { aliasedIcons, iconLibrary, uiIcons } from '@/shared/icons';
import { timeState } from '@/shared/state/time.svelte';
import { DbResetType } from '@/shared/stores/db/enums';
import { dynamicDataStore } from '@/user-home/stores/dynamicData.svelte';
import type { Character } from '@/types';
import type { Chore, Task } from '@/types/tasks';
import { twoWeekDecorator } from './utils';
import { customResetPeriod } from '../custom-reset-period';

const specialAssignmentUnlocks = [
    94865, // Special Assignment: What Remains of a Temple Broken
    94866, // Special Assignment: Ours Once More!
    94390, // Special Assignment: A Hunter's Regret
    95435, // Special Assignment: Shade and Claw
    92848, // Special Assignment: The Grand Magister's Drink
    94391, // Special Assignment: Push Back the Light
    94795, // Special Assignment: Agents of the Shield
    94743, // Special Assignment: Precision Excision
];

const specialAssignmentUnlockToQuest: Record<number, number> = {
    94865: 91390, // Special Assignment: What Remains of a Temple Broken
    94866: 91796, // Special Assignment: Ours Once More!
    94390: 92063, // Special Assignment: A Hunter's Regret
    95435: 92139, // Special Assignment: Shade and Claw
    92848: 92145, // Special Assignment: The Grand Magister's Drink
    94391: 93013, // Special Assignment: Push Back the Light
    94795: 93244, // Special Assignment: Agents of the Shield
    94743: 93438, // Special Assignment: Precision Excision

    // Coiled Isle
    96492: 95921, // Special Assignment: Demand and Supply
    96029: 95922, // Special Assignment: Face the Swarm
    96307: 95918, // Special Assignment: Wraith Wrath
};

const specialAssignmentQuestToUnlock: Record<number, number> = Object.fromEntries(
    Object.entries(specialAssignmentUnlockToQuest).map(([k, v]) => [v, parseInt(k)])
);

export const specialAssignmentFunc = (unlocks: number[], index: number, isQuest: boolean) => {
    return (char: Character, chore: Chore) => {
        const now = timeState.slowTime;
        const allWorldQuests = dynamicDataStore.getCachedQuests(char.region);
        const questIdIndexes = Object.fromEntries(
            allWorldQuests
                .filter((worldQuest) => worldQuest.expires > now)
                .map((worldQuest, index) => [worldQuest.questId, index])
        );

        const activeQuests: [number, number][] = [];
        for (const unlockQuestId of unlocks) {
            if (questIdIndexes[unlockQuestId]) {
                activeQuests.push([questIdIndexes[unlockQuestId], unlockQuestId]);
            } else {
                // Midnight 12.1 world quests have the actual quest as active, not the unlock
                const actualQuestId = specialAssignmentUnlockToQuest[unlockQuestId];
                if (actualQuestId && questIdIndexes[actualQuestId]) {
                    activeQuests.push([questIdIndexes[actualQuestId], unlockQuestId]);
                }
            }
        }

        activeQuests.sort((a, b) => a[0] - b[0]);

        if (activeQuests[index]) {
            return [
                isQuest
                    ? specialAssignmentUnlockToQuest[activeQuests[index][1]]
                    : activeQuests[index][1],
            ];
        } else {
            return unlocks;
        }
    };
};

export const specialAssignmentExpiry: Chore['customExpiryFunc'] = (
    chore,
    char,
    scannedAt,
    questIds
) => {
    if (!questIds || questIds?.length === 0) {
        return Constants.defaultTime;
    }

    const allWorldQuests = dynamicDataStore.getCachedQuests(char.region);
    const findQuestId = specialAssignmentQuestToUnlock[questIds[0]] || questIds[0];
    const worldQuest = allWorldQuests.find((wq) => wq.questId === findQuestId);
    return worldQuest?.expires || Constants.defaultTime;
};

export const midChores12_0: Task = {
    key: 'midChores12_0',
    name: '[Mid] 12.0.x',
    shortName: '12.0',
    showSeparate: true,
    chores: [
        {
            key: 'midHopeUnity',
            name: 'Hope/Unity',
            icon: aliasedIcons.planet,
            questReset: DbResetType.Custom,
            questResetForced: true,
            showQuestName: true,
            sumObjectives: true,
            customExpiryFunc: (chore, char, scannedAt) =>
                customResetPeriod(char, scannedAt, 1079, 2),
            decorationFunc: twoWeekDecorator,
            questIds: (char) =>
                char.level < 90
                    ? [95468]
                    : [
                          93890, // Midnight: Abundance
                          93767, // Midnight: Arcantina
                          94457, // Midnight: Battlegrounds
                          93909, // Midnight: Delves
                          93911, // Midnight: Dungeons
                          93769, // Midnight: Housing
                          93891, // Midnight: Legends of the Haranir
                          96727, // Midnight: Offworld Showdowns
                          93910, // Midnight: Prey
                          93912, // Midnight: Raid
                          95843, // Midnight: Ritual Sites
                          93889, // Midnight: Saltheril's Soiree
                          93892, // Midnight: Stormarion Assault
                          98232, // Midnight: Vaults of Atal'Utek
                          95842, // Midnight: Void Assaults
                          93913, // Midnight: World Boss
                          93766, // Midnight: World Quests
                      ],
        },
        {
            key: 'midAbundance',
            name: 'Abundance',
            icon: iconLibrary.gameTwirlyFlower,
            minimumLevel: 80,
            showQuestName: true,
            questReset: DbResetType.Weekly,
            questIds: [89507],
        },
        {
            key: 'midSoiree',
            name: 'Soiree',
            icon: iconLibrary.mdiPartyPopper,
            minimumLevel: 80,
            showQuestName: true,
            questReset: DbResetType.Weekly,
            questIds: [
                90573, // Fortify the Runestones: Magisters
                90574, // Fortify the Runestones: Blood Knights
                90575, // Fortify the Runestones: Farstriders
                90576, // Fortify the Runestones: Shades of the Row                        { quest = 89507 }, -- Abundant Offerings
            ],
        },
        {
            key: 'midStormarion',
            name: 'Stormarion',
            icon: iconLibrary.gameTeslaTurret,
            minimumLevel: 80,
            showQuestName: true,
            questReset: DbResetType.Weekly,
            questIds: [90962],
        },
        {
            key: 'midResearch',
            name: 'Void Research',
            icon: iconLibrary.gameFizzingFlask,
            minimumLevel: 80,
            showQuestName: true,
            questReset: DbResetType.Weekly,
            questIds: [94790],
        },
        {
            key: 'midSpecial1',
            name: 'Special Assignment 1',
            icon: iconLibrary.mdiNumeric1CircleOutline,
            showQuestName: true,
            questReset: DbResetType.Custom,
            customExpiryFunc: specialAssignmentExpiry,
            subChores: [
                {
                    key: 'unlock',
                    name: 'World Quests',
                    alwaysStarted: true,
                    overrideNeed: 3,
                    questIds: specialAssignmentFunc(specialAssignmentUnlocks, 0, false),
                    customExpiryFunc: specialAssignmentExpiry,
                },
                {
                    key: 'assignment',
                    name: 'Assignment',
                    alwaysStarted: true,
                    showQuestName: true,
                    questIds: specialAssignmentFunc(specialAssignmentUnlocks, 0, true),
                    customExpiryFunc: specialAssignmentExpiry,
                },
            ],
        },
        {
            key: 'midSpecial2',
            name: 'Special Assignment 2',
            icon: iconLibrary.mdiNumeric2CircleOutline,
            showQuestName: true,
            questReset: DbResetType.Custom,
            customExpiryFunc: specialAssignmentExpiry,
            subChores: [
                {
                    key: 'unlock',
                    name: 'World Quests',
                    alwaysStarted: true,
                    overrideNeed: 3,
                    questIds: specialAssignmentFunc(specialAssignmentUnlocks, 1, false),
                    customExpiryFunc: specialAssignmentExpiry,
                },
                {
                    key: 'assignment',
                    name: 'Assignment',
                    alwaysStarted: true,
                    showQuestName: true,
                    questIds: specialAssignmentFunc(specialAssignmentUnlocks, 1, true),
                    customExpiryFunc: specialAssignmentExpiry,
                },
            ],
        },
        {
            key: 'midEchoesRekindled',
            name: '[Har] Echoes Rekindled',
            icon: iconLibrary.gameSmallFire,
            minimumLevel: 80,
            questReset: DbResetType.Weekly,
            questIds: [92713],
        },
        // {
        //     key: 'midLostLegends',
        //     name: '[Har] Lost Legends',
        //     icon: iconLibrary.gameSecretBook,
        //     minimumLevel: 83,
        //     accountWide: true,
        //     questReset: DbResetType.Weekly,
        //     questIds: [89268],
        // },
        {
            key: 'midDungeon',
            name: 'Dungeon',
            icon: iconLibrary.faDungeon,
            minimumLevel: 90,
            accountWide: true,
            showQuestName: true,
            questResetForced: true,
            questReset: DbResetType.Weekly,
            questIds: [
                93751, // Windrunner Spire
                93752, // Murder Row
                93753, // Magisters' Terrace
                93754, // Maisara Caverns
                93755, // Den of Nalorakk
                93756, // The Blinding Vale
                93757, // Voidscar Arena
                93758, // Nexus-Point Xenas
            ],
        },
        {
            key: 'midWorldBossFirst',
            name: 'World Boss (First)',
            icon: iconLibrary.emojiZzz,
            minimumLevel: 90,
            accountWide: true,
            questReset: DbResetType.Weekly,
            questIds: [
                92127,
                92128,
                92129, // probably
                92130,
            ],
        },
        {
            key: 'midWorldBoss',
            name: 'World Boss',
            icon: iconLibrary.emojiZzz,
            minimumLevel: 90,
            questReset: DbResetType.Weekly,
            questIds: [
                // TODO from AreaPOI.db2, verify
                92123, // Cragpine
                92560, // Lu'ashal
                92636, // Predaxas
                92034, // Thorm'belan
            ],
        },
        {
            key: 'voidAssaults',
            name: 'Void Assaults',
            icon: iconLibrary.gameAbstract024,
            minimumLevel: 80,
            questReset: DbResetType.Weekly,
            questIds: [
                94385, // Void Assaults: Eversong Woods
                94386, // Void Assaults: Zul'Aman
            ],
        },
        {
            key: 'showdownNormal',
            name: 'Showdown [N]',
            icon: 'S:squareN:',
            minimumLevel: 80,
            questReset: DbResetType.Weekly,
            questResetForced: true,
            subChores: [
                {
                    key: 'leveling',
                    name: 'Leveling',
                    maximumLevel: 89,
                    questIds: [
                        96716, // Showdown on Val
                        96720, // Showdown on Naigtal
                    ],
                },
                {
                    key: 'max',
                    name: 'Max Level',
                    minimumLevel: 90,
                    questIds: [
                        96713, // Showdown on Val
                        96717, // Showdown on Naigtal
                    ],
                },
            ],
        },
        {
            key: 'showdownHeroic',
            name: 'Showdown [H]',
            icon: 'S:squareH:',
            minimumLevel: 90,
            questReset: DbResetType.Weekly,
            questResetForced: true,
            questIds: [
                96714, // Showdown on Val: Heroic
                96718, // Showdown on Naigtal: Heroic
            ],
        },
        {
            key: 'naigtalValWorldBossNormal',
            name: 'Naigtal/Val World Boss [N]',
            icon: 'B:squareN:',
            minimumLevel: 80,
            questReset: DbResetType.Weekly,
            questResetForced: true,
            questIds: [
                96522, // Naigtal
                96295, // Val
            ],
        },
        {
            key: 'naigtalValWorldBossHeroic',
            name: 'Naigtal/Val World Boss [H]',
            icon: 'B:squareH:',
            minimumLevel: 90,
            questReset: DbResetType.Weekly,
            questResetForced: true,
            questIds: [
                96942, // Naigtal
                96941, // Val
            ],
        },
    ],
};
